import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner, Row, Col } from "react-bootstrap";
import useApi from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { resolveAssetUrl } from "../utils/assetUrl";

const Services = () => {
  const { user, getToken } = useAuth();
  const { publicAuth, bearerAuth } = useApi();
  const razorpayKey = process.env.REACT_APP_RAZORPAY_KEY || "";

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  
  // Booking form states
  const [bookingForm, setBookingForm] = useState({
    scheduled_date: null,
    scheduled_time: null,
    contact_name: "",
    contact_phone: "",
    contact_email: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zipcode: "",
    payment_method: "razorpay",
    notes: ""
  });

  const { currentCity } = useAuth();

  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const getPriceInfo = (service) => {
    const basePrice = num(service.base_price ?? service.price ?? 0);
    const baseFinalPrice = num(
      service.base_final_price ?? service.basePriceFinal ?? service.base_final ?? basePrice
    );
    const discountPercent = num(service.discount_percent ?? service.discount ?? 0);
    const salePrice = num(
      service.sale_price ??
      (basePrice - (basePrice * discountPercent) / 100)
    );
    const finalPrice = num(service.final_price ?? salePrice);
    const discountAmount = Math.max(0, baseFinalPrice - finalPrice);
    return { basePrice, baseFinalPrice, salePrice, finalPrice, discountPercent, discountAmount };
  };

  // Calculate total price
  const calculateTotalPrice = () => {
    return selectedServices.reduce((total, service) => {
      const { finalPrice } = getPriceInfo(service);
      return total + finalPrice;
    }, 0);
  };

  // Calculate total discount
  const calculateTotalDiscount = () => {
    return selectedServices.reduce((total, service) => {
      const { discountAmount } = getPriceInfo(service);
      return total + discountAmount;
    }, 0);
  };

  const createPaymentKey = () =>
    `service_${Date.now()}_${Math.random().toString(36).slice(2)}`;

  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error("Unable to load Razorpay checkout."));
      document.body.appendChild(script);
    });

  const servicePaymentRequest = (url, payload) => {
    if (user) {
      return bearerAuth(url, "POST", payload);
    }
    return publicAuth(url, "POST", payload);
  };

  const startServicePayment = async (bookingData) => {
    await loadRazorpayScript();
    const orderRes = await servicePaymentRequest("service/payments/razorpay/order", bookingData);
    const order = orderRes?.data?.data;

    if (!order?.order_id) {
      throw new Error(orderRes?.data?.message || "Unable to create payment order.");
    }

    const checkoutKey = order.key || razorpayKey;
    if (!checkoutKey) {
      throw new Error("Razorpay key is not configured.");
    }

    return new Promise((resolve, reject) => {
      const razorpay = new window.Razorpay({
        key: checkoutKey,
        amount: order.amount_paisa,
        currency: order.currency || "INR",
        name: "Asysta",
        description: "Service Booking Payment",
        order_id: order.order_id,
        prefill: {
          name: bookingData.contact_name || user?.full_name || "",
          email: bookingData.contact_email || user?.email || "",
          contact: bookingData.contact_phone || user?.contact || "",
        },
        handler: async (response) => {
          try {
            const verifyRes = await servicePaymentRequest("service/payments/razorpay/verify", {
              vendor_id: bookingData.vendor_id,
              service_id: bookingData.service_id,
              payment_key: bookingData.payment_key,
              contact_email: bookingData.contact_email,
              contact_phone: bookingData.contact_phone,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            const verified = verifyRes?.data?.data;
            if (!verifyRes?.data?.status || !verified?.payment_token) {
              throw new Error(verifyRes?.data?.message || "Payment verification failed.");
            }
            resolve(verified);
          } catch (err) {
            reject(new Error(err?.response?.data?.message || err?.message || "Payment verification failed."));
          }
        },
        modal: {
          ondismiss: () => reject(new Error("Payment was cancelled.")),
        },
        theme: { color: "#284eb8" },
      });

      razorpay.on("payment.failed", (response) => {
        reject(new Error(response?.error?.description || "Payment failed."));
      });

      razorpay.open();
    });
  };

  // Fetch Vendors
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        setLoading(true);
        setError("");
        let url = "vendors";
        if (currentCity) url += `?city=${encodeURIComponent(currentCity)}`;

        const res = await publicAuth(url);
        const raw = res?.data?.data ?? res?.data?.results ?? res?.data ?? [];
        setVendors(Array.isArray(raw) ? raw : []);
      } catch (err) {
        console.error(err);
        setError("Failed to load vendors");
      } finally {
        setLoading(false);
      }
    };
    fetchVendors();
  }, [currentCity]);

  // Initialize booking form based on login status
  useEffect(() => {
    if (showModal) {
      if (user) {
        // User is logged in - populate with user data
        setBookingForm(prev => ({
          ...prev,
          contact_name: user.full_name || "",
          contact_phone: String(user.contact || ""), // Convert to string
          contact_email: user.email || "",
          city: user.city || currentCity || "",
          zipcode: String(user.zipcode || "") // Convert to string
        }));
      } else {
        // User is not logged in - clear form
        setBookingForm({
          scheduled_date: null,
          scheduled_time: null,
          contact_name: "",
          contact_phone: "",
          contact_email: "",
          address_line1: "",
          address_line2: "",
          city: currentCity || "",
          state: "",
          zipcode: "",
          payment_method: "razorpay",
          notes: ""
        });
      }
    }
  }, [user, showModal, currentCity]);

  // Open booking modal
  const handleOpenModal = (vendor) => {
    setSelectedVendor(vendor);
    setSelectedServices([]);
    setShowModal(true);
  };

  // Handle service selection
  const handleCheckboxChange = (service) => {
    setSelectedServices((prev) =>
      prev.includes(service)
        ? prev.filter((s) => s !== service)
        : [...prev, service]
    );
  };

  // Select all services
  const handleSelectAll = () => {
    if (selectedServices.length === selectedVendor?.services?.length) {
      setSelectedServices([]);
    } else {
      setSelectedServices([...selectedVendor.services]);
    }
  };

  // Handle form input changes - FIXED for phone number
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone and zipcode to ensure they are strings
    if (name === 'contact_phone' || name === 'zipcode') {
      // Remove non-numeric characters and ensure it's a string
      const numericValue = value.replace(/\D/g, '');
      setBookingForm(prev => ({
        ...prev,
        [name]: numericValue
      }));
    } else {
      setBookingForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle date/time changes
  const handleDateChange = (date) => {
    setBookingForm(prev => ({
      ...prev,
      scheduled_date: date
    }));
  };

  const handleTimeChange = (time) => {
    setBookingForm(prev => ({
      ...prev,
      scheduled_time: time
    }));
  };

  // Handle booking submission - FIXED for phone string issue
  const handleBookNow = async () => {
    if (selectedServices.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Select Services!",
        text: "Please select at least one service to book.",
      });
      return;
    }

    // Validate phone number
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(bookingForm.contact_phone)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Phone Number!",
        text: "Please enter a valid 10-digit phone number.",
      });
      return;
    }

    if (!bookingForm.contact_name || !bookingForm.contact_phone) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information!",
        text: "Please fill in your contact details.",
      });
      return;
    }

    if (!bookingForm.scheduled_date) {
      Swal.fire({
        icon: "warning",
        title: "Select Date!",
        text: "Please select a date for service.",
      });
      return;
    }

    try {
      setBookingLoading(true);

      // Format all data to ensure proper types
      const formatString = (value) => {
        if (value === null || value === undefined) return "";
        return String(value).trim();
      };

      const formatPhone = (value) => {
        if (value === null || value === undefined) return "";
        return String(value).trim();
      };

      // Prepare booking data with proper string formatting
      const bookingData = {
        vendor_id: selectedVendor.id,
        service_id: selectedServices.map(service => service.id), // Array of service IDs
        payment_key: createPaymentKey(),
        scheduled_date: bookingForm.scheduled_date 
          ? bookingForm.scheduled_date.toISOString().split('T')[0]
          : null,
        scheduled_time: bookingForm.scheduled_time
          ? bookingForm.scheduled_time.toTimeString().split(' ')[0].substring(0, 5)
          : null,
        contact_name: formatString(bookingForm.contact_name),
        contact_phone: formatPhone(bookingForm.contact_phone), // Ensure string
        contact_email: formatString(bookingForm.contact_email),
        address_line1: formatString(bookingForm.address_line1),
        address_line2: formatString(bookingForm.address_line2),
        city: formatString(bookingForm.city),
        state: formatString(bookingForm.state),
        zipcode: formatString(bookingForm.zipcode), // Ensure string
        // pricing is computed server-side to avoid mismatch
        payment_method: "razorpay",
        notes: formatString(bookingForm.notes)
      };

      console.log("Booking Data:", bookingData);
      console.log("Phone type:", typeof bookingData.contact_phone);
      console.log("Phone value:", bookingData.contact_phone);

      const paymentData = await startServicePayment(bookingData);
      bookingData.payment_token = paymentData.payment_token;

      // Make API call - use bearerAuth for logged in users, publicAuth for guests
      let response;
      if (user) {
        response = await bearerAuth("service/book", "POST", bookingData);
      } else {
        // For non-logged in users
        response = await publicAuth("service/book", "POST", bookingData);
      }

      const result = response?.data || {};

      if (result.status === true || response.status === 200 || response.status === 201) {
        Swal.fire({
          icon: "success",
          title: "Booking Confirmed!",
          html: `
            <div class="text-left">
              <p><strong>Booking ID:</strong> ${result.data?.booking?.booking_code || result.data?.id || 'N/A'}</p>
              <p><strong>Vendor:</strong> ${selectedVendor.vendor_name}</p>
              <p><strong>Services Booked:</strong> ${selectedServices.length}</p>
              <p><strong>Paid Amount:</strong> ₹${Number(paymentData?.amount || calculateTotalPrice()).toFixed(2)}</p>
              <p><strong>You Saved:</strong> ₹${calculateTotalDiscount().toFixed(2)}</p>
              <p><strong>Scheduled Date:</strong> ${bookingForm.scheduled_date.toLocaleDateString()}</p>
              <p><strong>Contact:</strong> ${bookingForm.contact_phone}</p>
              <hr>
              <small class="text-muted">You will receive a confirmation message shortly.</small>
            </div>
          `,
          confirmButtonText: "OK",
          showCancelButton: true,
          cancelButtonText: "Close"
        }).then((result) => {
          
          setShowModal(false);
          setSelectedServices([]);
        });
      } else {
        throw new Error(result.message || "Booking failed");
      }
    } catch (err) {
      console.error("Booking error:", err);
      console.error("Error response:", err.response?.data);
      
      // Show detailed error message
      let errorMessage = err.message || "Failed to book service. Please try again.";
      
      if (err.response?.data) {
        // If API returns specific error messages
        if (err.response.data.errors) {
          errorMessage = Object.values(err.response.data.errors).flat().join(', ');
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }
      
      Swal.fire({
        icon: "error",
        title: "Booking Failed",
        html: `<div class="text-left">
          <p>${errorMessage}</p>
          ${!user ? '<small class="text-muted">Note: Some vendors may require login for booking.</small>' : ''}
        </div>`,
      });
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <>
      <section className="service-details pt-100 pb-70">
        <div className="auto-container">
          <div className="sec-title text-center mb-5">
            <h2 data-animation="fadeInUp">Our Vendors</h2>
            <p
              data-animation="fadeInUp"
              data-delay="100"
              className="text-muted"
            >
              Discover professional services from trusted vendors
            </p>
          </div>

          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading vendors...</p>
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-5">
              <div className="alert alert-danger d-inline-block">{error}</div>
            </div>
          )}

          {!loading && !error && vendors.length === 0 && (
            <div className="text-center py-5">
              <div className="alert alert-info d-inline-block">
                No vendors found in your area
              </div>
            </div>
          )}

          {!loading && !error && vendors.length > 0 && (
            <div className="row clearfix">
              {vendors.map((vendor, index) => (
                <div
                  key={vendor.id}
                  className="col-12 mb-5 animated-section"
                  data-animation="fadeInUp"
                  data-delay={index * 100}
                >
                  <div className="vendor-card card border-0 shadow-sm h-100">
                    <div className="row g-0 h-100">
                      {/* Vendor Image */}
                      <div className="col-md-4">
                        <div className="vendor-image h-100">
                          <img
                            src={
                              resolveAssetUrl(
                                vendor.photo_url || vendor.photo,
                                `${process.env.PUBLIC_URL}/assets/images/service/service-1.jpg`
                              )
                            }
                            alt={vendor.vendor_name}
                            className="img-fluid h-100 w-100"
                            onError={(e) =>
                              (e.target.src =
                                process.env.PUBLIC_URL + "/assets/images/service/service-1.jpg")
                            }
                            style={{
                              objectFit: "cover",
                              borderTopLeftRadius: "12px",
                              borderBottomLeftRadius: "12px",
                            }}
                          />
                        </div>
                      </div>

                      {/* Vendor Details */}
                      <div className="col-md-8">
                        <div className="card-body h-100 d-flex flex-column p-4">
                          <div className="mb-3">
                            <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill">
                              <i className="fas fa-briefcase me-2"></i>
                              {vendor.business_type || "Service Provider"}
                            </span>
                          </div>

                          <div className="services-list flex-grow-1">
                            <h5 className="card-title mb-4 text-dark">
                              <i className="fas fa-list me-2 text-primary"></i>
                              Available Services
                            </h5>

                            <div className="row g-3">
                              {Array.isArray(vendor.services) &&
                              vendor.services.length > 0 ? (
                                vendor.services.map((service, sIndex) => (
                                  <div key={sIndex} className="col-lg-6 col-md-12">
                                    <div className="service-item d-flex justify-content-between align-items-center p-3 border rounded bg-light">
                                      <div className="service-info">
                                        <h6 className="mb-1 text-dark">
                                          {service.service_name}
                                        </h6>
                                        <div className="price-info">
                                          {getPriceInfo(service).discountPercent > 0 ? (
                                            <>
                                              <span className="text-muted text-decoration-line-through me-2">
                                                ₹{getPriceInfo(service).baseFinalPrice.toFixed(2)}
                                              </span>
                                              <span className="text-success fw-bold">
                                                ₹{getPriceInfo(service).finalPrice.toFixed(2)}
                                              </span>
                                              <small className="text-danger ms-2">
                                                (₹{getPriceInfo(service).discountAmount.toFixed(2)} off)
                                              </small>
                                            </>
                                          ) : (
                                            <span className="text-success fw-bold">
                                              ₹{getPriceInfo(service).finalPrice.toFixed(2)}
                                            </span>
                                          )}
                                        </div>
                                        {service.duration && (
                                          <small className="text-muted d-block">
                                            <i className="fas fa-clock me-1"></i>
                                            {service.duration}
                                          </small>
                                        )}
                                        <small className="text-muted d-block">
                                          <i className="fas fa-tag me-1"></i>
                                          {service.services_category || "General"}
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="col-12">
                                  <p className="text-muted fst-italic">
                                    No services listed for this vendor.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="vendor-meta row mt-4 pt-3 border-top">
                            <div className="col-md-6">
                              <div className="d-flex align-items-center text-muted mb-2">
                                <i className="fas fa-award text-warning me-2"></i>
                                <span>
                                  {vendor.experience_years || "0"} years experience
                                </span>
                              </div>
                              <div className="d-flex align-items-center text-muted">
                                <i className="fas fa-map-marker-alt text-danger me-2"></i>
                                <span>
                                  {vendor.address ||
                                    vendor.city ||
                                    "Address not specified"}
                                </span>
                              </div>
                            </div>
                            <div className="col-md-6 text-md-end">
                              <div className="d-flex align-items-center justify-content-md-end text-muted mb-2">
                                <i className="fas fa-phone text-success me-2"></i>
                                <span>{vendor.phone || "N/A"}</span>
                              </div>
                              <div className="d-flex align-items-center justify-content-md-end text-muted">
                                <i className="fas fa-envelope text-info me-2"></i>
                                <span>{vendor.email || "N/A"}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="card-footer bg-transparent border-top-0 py-3">
                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <h4 className="mb-0 text-dark">
                            <i className="fas fa-user-tie me-2 text-primary"></i>
                            {vendor.vendor_name || "Unnamed Vendor"}
                          </h4>
                          {vendor.business_name && (
                            <p className="text-muted mb-0 mt-1">
                              {vendor.business_name}
                            </p>
                          )}
                        </div>
                        <div className="col-md-4 text-md-end">
                          <Button
                            variant="primary"
                            className="px-4 py-2"
                            onClick={() => handleOpenModal(vendor)}
                          >
                            <i className="fas fa-calendar-check me-2"></i>
                            Book Now
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Booking Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-calendar-alt me-2"></i>
            Book Services - {selectedVendor?.vendor_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Login/User Status Info */}
          {user ? (
            <div className="alert alert-info mb-4">
              <div className="d-flex align-items-center">
                <i className="fas fa-user-check fa-lg me-3"></i>
                <div>
                  <strong>Logged in as: {user.username}</strong>
                  <div className="small">
                    Your details are auto-filled below
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="alert alert-warning mb-4">
              <div className="d-flex align-items-center">
                <i className="fas fa-user-clock fa-lg me-3"></i>
                <div>
                  <strong>You're booking as a guest</strong>
                  <div className="small">
                    Please fill in your details. 
                    {/* <Button 
                      variant="link" 
                      className="p-0 ms-1"
                      onClick={() => {
                        setShowModal(false);
                        window.location.href = '/login';
                      }}
                    >
                      Login
                    </Button> for faster booking. */}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Selected Services Section */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <i className="fas fa-list-check me-2 text-primary"></i>
                Select Services
              </h6>
              {selectedVendor?.services?.length > 0 && (
                <Button 
                  variant="outline-primary" 
                  size="sm"
                  onClick={handleSelectAll}
                >
                  {selectedServices.length === selectedVendor?.services?.length 
                    ? "Deselect All" 
                    : "Select All"}
                </Button>
              )}
            </div>
            
            {selectedVendor?.services && selectedVendor.services.length > 0 ? (
              <Form>
                <Row>
                  {selectedVendor.services.map((service, idx) => (
                    <Col md={6} key={idx} className="mb-3">
                      <div className={`border rounded p-3 h-100 ${selectedServices.includes(service) ? 'border-primary bg-primary-light' : ''}`}>
                        <Form.Check
                          type="checkbox"
                          id={`service-${idx}`}
                          label={
                            <div className="w-100">
                              <strong className="d-block">{service.service_name}</strong>
                              <div className="mt-2">
                                {getPriceInfo(service).discountPercent > 0 ? (
                                  <>
                                    <span className="text-decoration-line-through text-muted me-2">
                                      ₹{getPriceInfo(service).baseFinalPrice.toFixed(2)}
                                    </span>
                                    <span className="text-success fw-bold">
                                      ₹{getPriceInfo(service).finalPrice.toFixed(2)}
                                    </span>
                                    <small className="text-danger ms-2">
                                      (₹{getPriceInfo(service).discountAmount.toFixed(2)} off)
                                    </small>
                                  </>
                                ) : (
                                  <span className="text-success fw-bold">
                                    ₹{getPriceInfo(service).finalPrice.toFixed(2)}
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 small">
                                {service.duration && (
                                  <span className="me-3">
                                    <i className="fas fa-clock me-1"></i>
                                    {service.duration}
                                  </span>
                                )}
                                <span>
                                  <i className="fas fa-tag me-1"></i>
                                  {service.services_category || "General"}
                                </span>
                              </div>
                              {getPriceInfo(service).discountPercent > 0 && (
                                <div className="mt-1 small text-success">
                                  <i className="fas fa-badge-percent me-1"></i>
                                  Save ₹{getPriceInfo(service).discountAmount.toFixed(2)}
                                </div>
                              )}
                            </div>
                          }
                          onChange={() => handleCheckboxChange(service)}
                          checked={selectedServices.includes(service)}
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
              </Form>
            ) : (
              <p className="text-muted">No services available for this vendor.</p>
            )}
            
            {/* Selected Services Summary */}
            {selectedServices.length > 0 && (
              <div className="mt-3 p-3 bg-light rounded">
                <h6>
                  <i className="fas fa-shopping-cart me-2"></i>
                  Selected Services ({selectedServices.length})
                </h6>
                <ul className="mb-2">
                  {selectedServices.map((service, idx) => (
                    <li key={idx} className="mb-1">
                      <strong>{service.service_name}</strong> - 
                      ₹{getPriceInfo(service).finalPrice.toFixed(2)}
                      {getPriceInfo(service).discountPercent > 0 && (
                        <span className="text-danger ms-2">
                          (₹{getPriceInfo(service).discountAmount.toFixed(2)} off)
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="mt-2 pt-2 border-top">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong className="fs-5">
                        Total Amount: ₹{calculateTotalPrice().toFixed(2)}
                      </strong>
                      {calculateTotalDiscount() > 0 && (
                        <div className="small text-success">
                          You Save: ₹{calculateTotalDiscount().toFixed(2)}
                        </div>
                      )}
                    </div>
                    <div className="text-end">
                      <div className="small text-muted">
                        {selectedServices.length} service{selectedServices.length > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Booking Details Form */}
          <h6 className="mb-3">
            <i className="fas fa-calendar-day me-2 text-primary"></i>
            Booking Details
          </h6>
          <Form>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>
                    <i className="fas fa-calendar me-1"></i>
                    Date <span className="text-danger">*</span>
                  </Form.Label>
                  <DatePicker
                    selected={bookingForm.scheduled_date}
                    onChange={handleDateChange}
                    dateFormat="dd/MM/yyyy"
                    minDate={new Date()}
                    className="form-control"
                    placeholderText="Select date"
                    isClearable
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>
                    <i className="fas fa-clock me-1"></i>
                    Time
                  </Form.Label>
                  <DatePicker
                    selected={bookingForm.scheduled_time}
                    onChange={handleTimeChange}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={30}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className="form-control"
                    placeholderText="Select time"
                  />
                  <Form.Text className="text-muted">
                    Vendor's working hours: 9:00 AM - 8:00 PM
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <h6 className="mt-4 mb-3">
              <i className="fas fa-user-circle me-2 text-primary"></i>
              Contact Information
            </h6>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="contact_name"
                    value={bookingForm.contact_name}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="tel"
                    name="contact_phone"
                    value={bookingForm.contact_phone}
                    onChange={handleFormChange}
                    required
                    pattern="[0-9]{10}"
                    maxLength="10"
                    placeholder="10 digit mobile number"
                  />
                  <Form.Text className="text-muted">
                    10 digit mobile number (only numbers)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="contact_email"
                    value={bookingForm.contact_email}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Payment Method</Form.Label>
                  <Form.Select
                    name="payment_method"
                    value="razorpay"
                    disabled
                  >
                    <option value="razorpay">Online Payment (Razorpay)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h6 className="mt-4 mb-3">
              <i className="fas fa-map-marker-alt me-2 text-primary"></i>
              Service Address
            </h6>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Address Line 1 <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="address_line1"
                    value={bookingForm.address_line1}
                    onChange={handleFormChange}
                    placeholder="Street address, house no."
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Address Line 2</Form.Label>
                  <Form.Control
                    type="text"
                    name="address_line2"
                    value={bookingForm.address_line2}
                    onChange={handleFormChange}
                    placeholder="Apartment, suite, unit, building, floor etc."
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label>City <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={bookingForm.city}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label>State <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={bookingForm.state}
                    onChange={handleFormChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="mb-3">
                <Form.Group>
                  <Form.Label>Zip Code <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="zipcode"
                    value={bookingForm.zipcode}
                    onChange={handleFormChange}
                    required
                    pattern="[0-9]{6}"
                    maxLength="6"
                    placeholder="6 digit pincode"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>
                <i className="fas fa-sticky-note me-1"></i>
                Additional Notes
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={bookingForm.notes}
                onChange={handleFormChange}
                placeholder="Any special instructions, requirements, or notes for the vendor..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={bookingLoading}>
            <i className="fas fa-times me-2"></i>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleBookNow}
            disabled={selectedServices.length === 0 || bookingLoading || !bookingForm.scheduled_date}
            className="px-4"
          >
            {bookingLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Processing...
              </>
            ) : (
              <>
                <i className="fas fa-check-circle me-2"></i>
                {user ? 'Confirm Booking' : 'Book as Guest'} (₹{calculateTotalPrice().toFixed(2)})
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom CSS */}
      <style>{`
        .vendor-card {
          border-radius: 12px;
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .vendor-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
        }
        .service-item {
          transition: all 0.3s ease;
          border: 1px solid #e9ecef !important;
        }
        .service-item:hover {
          border-color: #007bff !important;
          background: #f8f9fa !important;
        }
        .price-info {
          font-size: 0.9rem;
        }
        .react-datepicker-wrapper {
          width: 100%;
        }
        .service-info {
          width: 100%;
        }
        .bg-primary-light {
          background-color: rgba(0, 123, 255, 0.05) !important;
        }
      `}</style>
    </>
  );
};

export default Services;










